import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronUp, GitCompare, Minus, Plus, Equal } from "lucide-react";

export interface RefineDiffSection {
  /** Stable key for the section */
  key: string;
  /** Human label, e.g. "Executive Summary" */
  label: string;
  /** Previous text (before refinement) */
  before: string;
  /** New text (after refinement) */
  after: string;
}

interface RefineDiffSummaryProps {
  sections: RefineDiffSection[];
  /** Optional title override */
  title?: string;
}

type ChangeKind = "added" | "removed" | "modified" | "unchanged";

interface SectionChange {
  key: string;
  label: string;
  kind: ChangeKind;
  before: string;
  after: string;
  /** Approximate character delta */
  delta: number;
}

const norm = (s: string) => (s || "").trim().replace(/\s+/g, " ");

const classify = (before: string, after: string): ChangeKind => {
  const b = norm(before);
  const a = norm(after);
  if (!b && a) return "added";
  if (b && !a) return "removed";
  if (b === a) return "unchanged";
  return "modified";
};

const KIND_META: Record<ChangeKind, { label: string; icon: typeof Plus; className: string }> = {
  added: {
    label: "New",
    icon: Plus,
    className: "bg-green-500/10 text-green-500 border-green-500/30",
  },
  modified: {
    label: "Updated",
    icon: GitCompare,
    className: "bg-primary/10 text-primary border-primary/30",
  },
  removed: {
    label: "Removed",
    icon: Minus,
    className: "bg-destructive/10 text-destructive border-destructive/30",
  },
  unchanged: {
    label: "Unchanged",
    icon: Equal,
    className: "bg-muted text-muted-foreground border-border",
  },
};

const truncate = (s: string, n = 220) => {
  const t = (s || "").trim();
  if (t.length <= n) return t;
  return `${t.slice(0, n)}…`;
};

const RefineDiffSummary = ({ sections, title = "What Changed in This Refinement" }: RefineDiffSummaryProps) => {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [showUnchanged, setShowUnchanged] = useState(false);

  const changes: SectionChange[] = useMemo(
    () =>
      sections.map((s) => ({
        key: s.key,
        label: s.label,
        kind: classify(s.before, s.after),
        before: s.before || "",
        after: s.after || "",
        delta: norm(s.after).length - norm(s.before).length,
      })),
    [sections],
  );

  const counts = useMemo(() => {
    const c = { added: 0, modified: 0, removed: 0, unchanged: 0 };
    changes.forEach((ch) => {
      c[ch.kind] += 1;
    });
    return c;
  }, [changes]);

  const visible = showUnchanged ? changes : changes.filter((c) => c.kind !== "unchanged");
  const totalChanged = counts.added + counts.modified + counts.removed;

  const toggle = (key: string) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  return (
    <Card className="border-primary/30 bg-card shadow-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-foreground text-base">
          <GitCompare className="h-5 w-5 text-primary" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="outline" className="bg-primary/10 text-primary border-primary/30">
            {totalChanged} section{totalChanged === 1 ? "" : "s"} changed
          </Badge>
          {counts.added > 0 && (
            <Badge variant="outline" className={KIND_META.added.className}>
              <Plus className="h-3 w-3 mr-1" /> {counts.added} new
            </Badge>
          )}
          {counts.modified > 0 && (
            <Badge variant="outline" className={KIND_META.modified.className}>
              <GitCompare className="h-3 w-3 mr-1" /> {counts.modified} updated
            </Badge>
          )}
          {counts.removed > 0 && (
            <Badge variant="outline" className={KIND_META.removed.className}>
              <Minus className="h-3 w-3 mr-1" /> {counts.removed} removed
            </Badge>
          )}
          {counts.unchanged > 0 && (
            <Badge variant="outline" className={KIND_META.unchanged.className}>
              <Equal className="h-3 w-3 mr-1" /> {counts.unchanged} unchanged
            </Badge>
          )}
        </div>

        {totalChanged === 0 && !showUnchanged && (
          <p className="text-sm text-muted-foreground">
            The AI didn't substantially change any sections. Try adding more specific details and regenerate again.
          </p>
        )}

        <div className="space-y-2">
          {visible.map((ch) => {
            const meta = KIND_META[ch.kind];
            const Icon = meta.icon;
            const isOpen = !!expanded[ch.key];
            const canExpand = ch.kind !== "unchanged" && (ch.before.trim() || ch.after.trim());
            return (
              <div key={ch.key} className="rounded-lg border border-border bg-secondary/40">
                <button
                  type="button"
                  onClick={() => canExpand && toggle(ch.key)}
                  className="w-full flex items-center justify-between gap-2 p-3 text-left hover:bg-secondary/60 transition-colors disabled:cursor-default"
                  disabled={!canExpand}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <Badge variant="outline" className={`${meta.className} shrink-0`}>
                      <Icon className="h-3 w-3 mr-1" />
                      {meta.label}
                    </Badge>
                    <span className="text-sm font-medium text-foreground truncate">{ch.label}</span>
                    {ch.kind !== "unchanged" && (
                      <span className="text-xs text-muted-foreground shrink-0">
                        {ch.delta > 0 ? `+${ch.delta}` : ch.delta} chars
                      </span>
                    )}
                  </div>
                  {canExpand && (
                    <span className="text-muted-foreground shrink-0">
                      {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </span>
                  )}
                </button>
                {isOpen && canExpand && (
                  <div className="border-t border-border p-3 space-y-3">
                    {ch.kind !== "added" && (
                      <div>
                        <p className="text-xs font-medium text-muted-foreground mb-1">Before</p>
                        <div className="text-xs whitespace-pre-wrap rounded-md bg-destructive/5 border border-destructive/20 p-2 text-foreground/80">
                          {truncate(ch.before) || <span className="italic text-muted-foreground">(empty)</span>}
                        </div>
                      </div>
                    )}
                    {ch.kind !== "removed" && (
                      <div>
                        <p className="text-xs font-medium text-muted-foreground mb-1">After</p>
                        <div className="text-xs whitespace-pre-wrap rounded-md bg-green-500/5 border border-green-500/20 p-2 text-foreground">
                          {truncate(ch.after) || <span className="italic text-muted-foreground">(empty)</span>}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {counts.unchanged > 0 && (
          <div className="pt-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setShowUnchanged((v) => !v)}
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              {showUnchanged ? "Hide unchanged sections" : `Show ${counts.unchanged} unchanged section${counts.unchanged === 1 ? "" : "s"}`}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default RefineDiffSummary;
